/* eslint-disable @typescript-eslint/no-magic-numbers */
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDismissableDialogProperties {
  isOpen: boolean;
  dismissable?: boolean;
  onClose: () => void;
}

const useDismissableDialog = ({
  isOpen,
  onClose,
  dismissable = true,
}: UseDismissableDialogProperties) => {
  const dialogReference = useRef<HTMLDialogElement>(null);
  const [hasOpened, setHasOpened] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setHasOpened(true);
      if (dismissable) window.history.pushState({ dialog: true }, '');
    } else {
      setHasOpened(false);
    }
  }, [dismissable, isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (!dismissable || !hasOpened) return;
      if (event.key === 'Escape') {
        window.history.back();
      }
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [dismissable, onClose, isOpen, hasOpened]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!dismissable || !hasOpened) return;
      if (dialogReference.current && !dialogReference.current.contains(event.target as Node)) {
        window.history.back();
      }
    };

    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dismissable, onClose, isOpen, hasOpened]);

  useEffect(() => {
    const handlePopState = () => {
      if (isOpen) {
        onClose();
      }
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [onClose, isOpen]);

  const onBrowserDismiss = useCallback(() => window.history.back(), []);
  return { dialogReference, onDismiss: dismissable ? onBrowserDismiss : onClose };
};

export default useDismissableDialog;
